import React from "react";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import LoadingDots from "../../../component/common/LoadingDots";

/**
 * 발행 탭 — 지금 상태가 무엇이고, 발행하면 무엇이 달라지는지 글로 설명한다.
 *
 * <p>아이콘·배지를 걷어낸 이유: 여기서 사람이 내리는 판단은 «이대로 직원들에게 내보내도 되나» 하나뿐인데,
 * 색과 아이콘이 많으면 그 문장을 읽기 전에 색부터 해석하게 된다. 읽어야 하는 화면이라 글로 쓴다.
 *
 * <p>네 덩어리로 나눈 건 사람이 각각 다르게 반응해야 하기 때문이다.
 * <b>막는 것</b>은 고쳐야 하고, <b>확인할 것</b>은 판단해야 하고, <b>일어날 일</b>은 알아야 하고,
 * <b>연결 상태</b>는 눈으로 대조해야 한다. 마지막이 특히 중요한데, 슬롯 오배치는 문법적으로 완벽해서
 * 검증으로 못 잡는다 — «공급가액» 칸에 합계금액을 붙여도 시스템은 모르고 부가세 뺀 금액이 올라갈 뿐이다.
 */
export default function PublishPanel({ check, preview, checking, publishing, onCheck, onPublish }) {
    if (checking) return <Box sx={{ py: 6 }}><LoadingDots /></Box>;

    if (!check) {
        return (
            <Box sx={{ maxWidth: 620 }}>
                <H>발행이란</H>
                <P>
                    지금 편집한 내용은 <B>초안</B>으로만 저장돼 있습니다. 직원들이 결재를 올릴 때는
                    아직 이전 내용이 나옵니다. <B>발행</B>해야 바뀐 양식이 적용됩니다.
                </P>
                <P>
                    발행하면 새 버전이 만들어집니다. 이미 올라가 진행 중인 결재는 자기가 상신될 때의 버전을
                    그대로 쓰므로, 결재 단계를 줄여도 진행 중인 문서가 중간에 끊기지 않습니다.
                </P>

                <Divider sx={{ my: 2.5 }} />

                <H>발행 전에 점검합니다</H>
                <P>
                    무엇이 잘못됐는지, 승인되면 어떤 일이 벌어지는지, 각 칸이 어느 시스템 값에
                    연결됐는지를 확인한 뒤 발행할 수 있습니다. 점검을 누르면 지금 화면의 내용이 먼저
                    저장됩니다.
                </P>
                <Button variant="contained" onClick={onCheck} sx={{ mt: 1.5, fontWeight: 700 }}>
                    저장하고 점검하기
                </Button>
            </Box>
        );
    }

    const blockers = check.blockers || [];
    const warnings = check.warnings || [];
    const effects  = check.effects || [];
    const slots    = check.slots || [];

    return (
        <Box sx={{ maxWidth: 720 }}>
            {/* 결론 한 문장 */}
            <Box sx={{ p: 2, borderRadius: 2, mb: 2.5,
                       border: "1px solid", borderColor: check.publishable ? "#bbf7d0" : "#fecaca",
                       bgcolor: check.publishable ? "#f0fdf4" : "#fef2f2" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800,
                                  color: check.publishable ? "#15803d" : "#b91c1c", mb: 0.5 }}>
                    {check.publishable ? "발행할 수 있습니다" : "먼저 고쳐야 할 것이 있습니다"}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "#475569", lineHeight: 1.7 }}>
                    {check.publishable
                        ? "아래 내용을 확인한 뒤 발행하세요. 발행하면 이후 올라오는 결재부터 이 양식이 적용됩니다."
                        : "아래 «막는 것» 을 해결하면 발행할 수 있습니다. 입력칸 탭에서 고친 뒤 다시 점검하세요."}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button variant="contained" disabled={!check.publishable || publishing}
                            onClick={onPublish} sx={{ fontWeight: 800 }}>
                        {publishing ? "발행하는 중…" : "발행"}
                    </Button>
                    <Button variant="outlined" onClick={onCheck} sx={{ fontWeight: 600 }}>
                        저장하고 다시 점검
                    </Button>
                </Stack>
            </Box>

            <Section
                title="막는 것"
                desc="이걸 해결하기 전에는 발행되지 않습니다."
                items={blockers}
                empty="없습니다."
                tone="#b91c1c"
            />

            <Section
                title="확인할 것"
                desc="발행은 되지만 사람이 판단해야 합니다."
                items={warnings}
                empty="없습니다."
                tone="#b45309"
            />

            <Section
                title="승인하면 일어나는 일"
                desc="이 양식으로 올라온 결재가 최종 승인될 때 다른 시스템에 생기는 변화입니다."
                items={effects}
                empty="아무 일도 일어나지 않습니다."
                tone="#334155"
            />

            {/* 연결 상태 — 검증이 못 잡는 자리라 표로 보여주고 눈으로 대조하게 한다 */}
            <Box sx={{ mb: 3 }}>
                <H>칸과 시스템 값의 연결</H>
                <P>
                    아래는 각 시스템 값이 <B>어느 칸에서</B> 값을 가져가는지입니다. 칸 이름은 자유롭게
                    바꿔도 되지만, 연결이 엉뚱한 칸을 가리키면 <B>아무 오류 없이</B> 잘못된 값이
                    재무·인사로 넘어갑니다. 여기만은 눈으로 확인해 주세요.
                </P>
                {slots.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>
                        연결된 값이 없습니다. 이 양식은 다른 시스템과 값을 주고받지 않습니다.
                    </Typography>
                ) : (
                    <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
                        {slots.map((s, i) => (
                            <Stack key={s.slot} direction="row" alignItems="center" spacing={1}
                                   sx={{ px: 1.75, py: 1, bgcolor: i % 2 ? "#fafafa" : "#fff",
                                         borderTop: i ? "1px solid #f1f5f9" : "none" }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", width: 160 }}>
                                    {s.slotLabel}
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
                                    «{s.fieldLabel}» 칸에서 가져갑니다
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                {s.required && (
                                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>필수 입력</Typography>
                                )}
                            </Stack>
                        ))}
                    </Box>
                )}
            </Box>

            {/* 자금수지 미리보기 */}
            {preview?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <H>자금수지에 어떻게 올라가는지</H>
                    <Box sx={{ p: 1.75, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                        {preview.map((line, i) => (
                            <Typography key={i} sx={{ fontSize: 12.5, color: "#334155", lineHeight: 1.9 }}>
                                {line}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function Section({ title, desc, items, empty, tone }) {
    const list = items || [];
    return (
        <Box sx={{ mb: 3 }}>
            <H>{title}{list.length > 0 ? ` (${list.length})` : ""}</H>
            <P>{desc}</P>
            {list.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>{empty}</Typography>
            ) : (
                <Stack spacing={0.75}>
                    {list.map((t, i) => (
                        <Typography key={i} sx={{ fontSize: 12.5, color: tone, lineHeight: 1.7,
                                                  pl: 1.5, borderLeft: `2px solid ${tone}33` }}>
                            {t}
                        </Typography>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

const H = ({ children }) => (
    <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a", mb: 0.5 }}>{children}</Typography>
);
const P = ({ children }) => (
    <Typography sx={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.8, mb: 1.25 }}>{children}</Typography>
);
const B = ({ children }) => (
    <Box component="span" sx={{ fontWeight: 800, color: "#334155" }}>{children}</Box>
);
